import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, Calendar, Plus, X, Search, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { mockArtists } from '@/lib/mockData';

const steps = [
  { id: 'basics', label: 'Basics', path: '/admin/events/new/basics' },
  { id: 'location', label: 'Location', path: '/admin/events/new/location' },
  { id: 'media', label: 'Media', path: '/admin/events/new/media' },
  { id: 'tickets', label: 'Tickets', path: '/admin/events/new/tickets' }
];

const StepIndicator = ({ currentStep }) => (
  <div className="flex items-center gap-2 mb-8">
    {steps.map((step, index) => (
      <React.Fragment key={step.id}>
        <div className={`flex items-center gap-2 ${
          step.id === currentStep ? 'text-primary' : 'text-muted-foreground'
        }`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
            step.id === currentStep 
              ? 'bg-primary text-primary-foreground' 
              : 'bg-secondary'
          }`}>
            {index + 1}
          </div>
          <span className="hidden md:inline text-sm">{step.label}</span>
        </div>
        {index < steps.length - 1 && (
          <div className="flex-1 h-px bg-border max-w-[60px]" />
        )}
      </React.Fragment>
    ))}
  </div>
);

// Add Artists Modal
const AddArtistsModal = ({ isOpen, onClose, selectedArtists, onSelectArtist }) => {
  const [searchQuery, setSearchQuery] = useState('');
  
  if (!isOpen) return null;

  const filteredArtists = mockArtists.filter(artist => 
    artist.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    artist.instrument?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const isSelected = (artistId) => selectedArtists.some(a => a.id === artistId);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="add-artists-modal">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-xl w-full max-w-lg max-h-[80vh] flex flex-col animate-in zoom-in-95">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border flex-shrink-0">
          <h2 className="text-xl font-bold text-white">Add Artists</h2>
          <button onClick={onClose} className="p-2 hover:bg-secondary rounded-lg">
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-border flex-shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search artists..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-secondary border-border pl-10"
            />
          </div>
        </div>

        {/* Artists List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {filteredArtists.map(artist => (
            <button
              key={artist.id}
              onClick={() => onSelectArtist(artist)}
              className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${
                isSelected(artist.id) 
                  ? 'bg-primary/20 border border-primary' 
                  : 'bg-secondary/50 hover:bg-secondary border border-transparent'
              }`}
              data-testid={`artist-option-${artist.id}`}
            >
              <img 
                src={artist.image} 
                alt={artist.name}
                className="w-12 h-12 rounded-full object-cover"
              />
              <div className="flex-1 text-left">
                <p className="font-medium text-white">{artist.name}</p>
                <p className="text-xs text-muted-foreground">{artist.instrument}</p>
              </div>
              {isSelected(artist.id) && (
                <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                  <span className="text-primary-foreground text-sm">✓</span>
                </div>
              )}
            </button>
          ))}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border flex-shrink-0">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-muted-foreground">
              {selectedArtists.length} artist{selectedArtists.length !== 1 ? 's' : ''} selected
            </span>
            <Link to="/admin/artists/new" className="text-primary text-sm hover:underline">
              + Create New Artist
            </Link>
          </div>
          <Button 
            onClick={onClose} 
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
          >
            Done
          </Button>
        </div>
      </div>
    </div>
  );
};

export function AdminCreateEventBasics() {
  const navigate = useNavigate();
  const [isArtistModalOpen, setIsArtistModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    livestreamEnabled: false,
    selectedArtists: []
  });

  const handleSelectArtist = (artist) => {
    const isAlreadySelected = formData.selectedArtists.some(a => a.id === artist.id);
    if (isAlreadySelected) {
      setFormData({
        ...formData,
        selectedArtists: formData.selectedArtists.filter(a => a.id !== artist.id)
      });
    } else {
      setFormData({
        ...formData,
        selectedArtists: [...formData.selectedArtists, artist]
      });
    }
  };

  const handleRemoveArtist = (artistId) => {
    setFormData({
      ...formData,
      selectedArtists: formData.selectedArtists.filter(a => a.id !== artistId)
    });
  };

  const handleNext = () => {
    navigate('/admin/events/new/location');
  };

  return (
    <AdminLayout>
      <div className="p-8 max-w-2xl mx-auto">
        <Link to="/admin" className="inline-flex items-center text-muted-foreground hover:text-white mb-6">
          <ChevronLeft className="w-5 h-5 mr-1" />
          Back to Events
        </Link>

        <h1 className="text-3xl font-bold text-white mb-2">Create Event</h1>
        <p className="text-muted-foreground mb-6">Fill in the basic details for your event</p>

        <StepIndicator currentStep="basics" />

        <Card className="bg-card border-border p-6">
          <div className="space-y-6">
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Event Title *</label>
              <Input 
                placeholder="Enter Event title"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                className="bg-secondary border-border"
                data-testid="event-title-input"
              />
            </div>

            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Event Date *</label>
              <div className="relative">
                <Input 
                  type="date"
                  placeholder="Select a date"
                  value={formData.date}
                  onChange={(e) => setFormData({...formData, date: e.target.value})}
                  className="bg-secondary border-border"
                  data-testid="event-date-input"
                />
                <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm text-white block">Enable Livestream</label>
                <p className="text-xs text-muted-foreground">Allow virtual attendance via livestream</p>
              </div>
              <Switch 
                checked={formData.livestreamEnabled}
                onCheckedChange={(checked) => setFormData({...formData, livestreamEnabled: checked})}
                data-testid="livestream-toggle"
              />
            </div>

            {/* Artists Section */}
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Artists</label>
              
              {/* Selected Artists */}
              {formData.selectedArtists.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {formData.selectedArtists.map(artist => (
                    <div 
                      key={artist.id}
                      className="flex items-center gap-2 px-3 py-1.5 bg-secondary rounded-full"
                    >
                      <img 
                        src={artist.image} 
                        alt={artist.name}
                        className="w-6 h-6 rounded-full object-cover"
                      />
                      <span className="text-sm text-white">{artist.name}</span>
                      <button 
                        onClick={() => handleRemoveArtist(artist.id)}
                        className="text-muted-foreground hover:text-white"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add Artists Button */}
              <Button 
                type="button"
                variant="outline" 
                className="w-full border-border border-dashed"
                onClick={() => setIsArtistModalOpen(true)}
                data-testid="add-artists-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Artists
              </Button>
            </div>

            <Button 
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-12"
              onClick={handleNext}
              data-testid="continue-btn"
            >
              Continue to Location
            </Button>
          </div>
        </Card>

        <p className="text-xs text-muted-foreground mt-4 text-center">
          Note: Ticket pricing (IRL General, IRL VIP, Livestream) will be configured in the Tickets step.
        </p>
      </div>

      {/* Add Artists Modal */}
      <AddArtistsModal
        isOpen={isArtistModalOpen}
        onClose={() => setIsArtistModalOpen(false)}
        selectedArtists={formData.selectedArtists}
        onSelectArtist={handleSelectArtist}
      />
    </AdminLayout>
  );
}

export default AdminCreateEventBasics;
